import { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { BASE_URL } from "../config";

const CreateOneTimePasswordModal = ({ isOpen, onClose, onCreated }) => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getAuthHeaders = () => {
    const token =
      sessionStorage.getItem("token") || localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const toAccountOption = (acc) => {
    const displayName =
      (acc.name || "").trim() ||
      `${acc.first_name || ""} ${acc.last_name || ""}`.trim() ||
      `Account ${acc.account_id}`;
    return { 
      value: acc.account_id,
      label: `${displayName} (${acc.email || "No email"})`,
    };
  };

  const loadAccountOptions = async (inputValue) => {
    const query = String(inputValue || "").trim();
    if (!query) return [];
    try {
      const params = new URLSearchParams({
        page: "1",
        limit: "25",
        search: query,
        sort: "name",
        dir: "asc",
        status: "active",
      });
      const res = await fetch(`${BASE_URL}/api/admin/accounts?${params}`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      return Array.isArray(data?.data) ? data.data.map(toAccountOption) : [];
    } catch (_e) {
      return [];
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    // default 1 day ahead
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setExpiryDate(tomorrow.toISOString().slice(0, 16));
    setSelectedAccount(null);
    setError("");
  }, [isOpen]);

  if (!isOpen) return null;

  const handleNext = async () => {
    if (!selectedAccount?.value) {
      setError("Please select an account.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${BASE_URL}/api/admin/accounts/create-otp`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          accountId: selectedAccount.value,
          expiryDate,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.message || "Failed to create one time password.");
        return;
      }

      onCreated(data.password);
    } catch (e) {
      setError("Failed to create one time password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white w-full max-w-[420px] p-4 sm:p-6 rounded shadow-lg">
        <h2 className="text-lg font-semibold mb-4">Create One Time Password</h2>

        <div className="mb-4">
          <label className="block text-sm text-gray-700 mb-1">
            Select Account
          </label>
          <AsyncSelect
            cacheOptions
            defaultOptions={false}
            isClearable
            value={selectedAccount}
            loadOptions={loadAccountOptions}
            onChange={setSelectedAccount}
            placeholder="Type name or email to search accounts"
            noOptionsMessage={({ inputValue }) =>
              inputValue
                ? "No matching accounts"
                : "Start typing to search accounts"
            }
          />
        </div>

        <input
          type="datetime-local"
          className="w-full border p-2 mb-4 rounded"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
        />

        {error ? <p className="text-red-600 text-sm mb-3">{error}</p> : null}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
          <button onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </button>
          <button
            onClick={handleNext}
            disabled={loading}
            className="w-full sm:w-auto text-red-600 font-semibold disabled:opacity-60"
          >
            {loading ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateOneTimePasswordModal;
